import {
  ClearingSaleBuyerStatusLabel,
  ClearingSaleBuyerStatusOrder,
} from "constants/clearingSaleAttributes";
import { ALL_PAYMENT_METHODS } from "constants/payments";
import {
  ALL_PRICING_TYPES,
  LivestockSalePricingTypeDisplayNameLkp,
} from "constants/pricingTypes";

import { getSaleExportSites } from "lib/sale";

import { ExportSite } from "./exportSites";
import { SaleType } from "./sale";

export enum GlobalSearchBooleanField {
  HasArrived = "hasArrived",
  HasBuyerExceptions = "hasBuyerExceptions",
  HasConsignmentScans = "hasConsignmentScans",
  HasMissingNasFields = "HasMissingNasFields",
  HasOverflow = "hasOverflow",
  HasProgeny = "hasProgeny",
  HasVendorExceptions = "hasVendorExceptions",
  HasWeight = "hasWeight",
  IsBuyerBalanced = "isBuyerBalanced",
  IsCounted = "isCounted",
  IsDelivered = "isDelivered",
  IsEidCompliant = "isEidCompliant",
  IsInProgress = "isInProgress",
  IsSold = "isSold",
  IsPreSaleVendorBalanced = "isPreSaleVendorBalanced",
  IsPostSaleVendorBalanced = "IsPostSaleVendorBalanced",
  IsConsignmentVideoUploaded = "IsConsignmentVideoUploaded",
  IsSaleLotVideoUploaded = "IsSaleLotVideoUploaded",
  IsConsignmentImageUploaded = "IsConsignmentImageUploaded",
  IsSaleLotImageUploaded = "IsSaleLotImageUploaded",
}

export const GlobalIntegrationsBooleanFields = {
  IsAuctionsPlusCompliant: ExportSite.AUCTIONS_PLUS,
  IsStockLiveCompliant: ExportSite.STOCK_LIVE,
} as const;

const ClearingSaleGlobalSearchBooleanFields = {
  HasBuyerExceptions: GlobalSearchBooleanField.HasBuyerExceptions,
  HasVendorExceptions: GlobalSearchBooleanField.HasVendorExceptions,
  IsSold: GlobalSearchBooleanField.IsSold,
} as const satisfies Record<string, GlobalSearchBooleanField>;

export enum SaleSearchField {
  SaleId = "saleId",
  Type = "saleType",
  Saleyard = "saleyard",
  BaseStatus = "baseStatus",
  Status = "status",
  Species = "species",
  Date = "date",
  Title = "title",
  Managers = "managers",
  Assignees = "assignees",
  EContractStatus = "eContractStatus",
}

export const GlobalSearchBooleanLabels = {
  [GlobalSearchBooleanField.HasArrived]: "Has Arrived",
  [GlobalSearchBooleanField.HasBuyerExceptions]: "Has Buyer Exceptions",
  [GlobalSearchBooleanField.HasConsignmentScans]: "Has Consignment Scans",
  [GlobalSearchBooleanField.HasMissingNasFields]: "Has Missing NAS Fields",
  [GlobalSearchBooleanField.HasOverflow]: "Has Overflow",
  [GlobalSearchBooleanField.HasProgeny]: "Has Progeny",
  [GlobalSearchBooleanField.HasVendorExceptions]: "Has Vendor Exceptions",
  [GlobalSearchBooleanField.HasWeight]: "Has Weight",
  [GlobalSearchBooleanField.IsBuyerBalanced]: "Is Buyer Balanced",
  [GlobalSearchBooleanField.IsCounted]: "Is Counted",
  [GlobalSearchBooleanField.IsDelivered]: "Is Delivered",
  [GlobalSearchBooleanField.IsEidCompliant]: "EID Compliant",
  [GlobalSearchBooleanField.IsInProgress]: "In Progress",
  [GlobalSearchBooleanField.IsSold]: "Sold",
  [GlobalSearchBooleanField.IsPreSaleVendorBalanced]:
    "Is Vendor Pre Sale Balanced",
  [GlobalSearchBooleanField.IsPostSaleVendorBalanced]:
    "Is Vendor Post Sale Balanced",
  [GlobalSearchBooleanField.IsConsignmentVideoUploaded]:
    "Is Consignment Video Uploaded",
  [GlobalSearchBooleanField.IsSaleLotVideoUploaded]:
    "Is Sale Lot Video Uploaded",
  [GlobalSearchBooleanField.IsConsignmentImageUploaded]:
    "Is Consignment Image Uploaded",
  [GlobalSearchBooleanField.IsSaleLotImageUploaded]:
    "Is Sale Lot Image Uploaded",
} as const satisfies Record<GlobalSearchBooleanField, string>;

const InverseGlobalSearchBooleanLabels = {
  [GlobalSearchBooleanField.HasArrived]: "Has Not Arrived",
  [GlobalSearchBooleanField.HasBuyerExceptions]: "Has No Buyer Exceptions",
  [GlobalSearchBooleanField.HasConsignmentScans]:
    "Doesn't Have Consignment Scans",
  [GlobalSearchBooleanField.HasMissingNasFields]:
    "Doesn't Have Missing NAS Fields",
  [GlobalSearchBooleanField.HasOverflow]: "Doesn't Have Overflow",
  [GlobalSearchBooleanField.HasProgeny]: "Doesn't Have Progeny",
  [GlobalSearchBooleanField.HasVendorExceptions]: "Has No Vendor Exceptions",
  [GlobalSearchBooleanField.HasWeight]: "Has No Weight",
  [GlobalSearchBooleanField.IsBuyerBalanced]: "Buyer Not Balanced",
  [GlobalSearchBooleanField.IsCounted]: "Is Not Counted",
  [GlobalSearchBooleanField.IsDelivered]: "Is Not Delivered",
  [GlobalSearchBooleanField.IsEidCompliant]: "EIDs Not Compliant",
  [GlobalSearchBooleanField.IsInProgress]: "Is Not In Progress",
  [GlobalSearchBooleanField.IsSold]: "Is Not Sold",
  [GlobalSearchBooleanField.IsPreSaleVendorBalanced]:
    "Vendor Not Pre Sale Balanced",
  [GlobalSearchBooleanField.IsPostSaleVendorBalanced]:
    "Vendor Not Post Sale Balanced",
  [GlobalSearchBooleanField.IsConsignmentVideoUploaded]:
    "Has No Consignment Videos Uploaded",
  [GlobalSearchBooleanField.IsSaleLotVideoUploaded]:
    "Has No Sale Lot Videos Uploaded",
  [GlobalSearchBooleanField.IsConsignmentImageUploaded]:
    "Has No Consignment Images Uploaded",
  [GlobalSearchBooleanField.IsSaleLotImageUploaded]:
    "Has No Sale Lot Images Uploaded",
} as const satisfies Record<GlobalSearchBooleanField, string>;

const GlobalIntegrationsBooleanLabels = {
  [GlobalIntegrationsBooleanFields.IsAuctionsPlusCompliant]: "Auctions Plus",
  [GlobalIntegrationsBooleanFields.IsStockLiveCompliant]: "Stock Live",
} as const satisfies Record<
  ValueInObj<typeof GlobalIntegrationsBooleanFields>,
  string
>;

const InverseGlobalIntegrationsBooleanLabels = {
  [GlobalIntegrationsBooleanFields.IsAuctionsPlusCompliant]:
    "Not Auctions Plus",
  [GlobalIntegrationsBooleanFields.IsStockLiveCompliant]: "Not Stock Live",
} as const satisfies Record<
  ValueInObj<typeof GlobalIntegrationsBooleanFields>,
  string
>;

const ClearingSaleGlobalSearchBooleanLabels = {
  [GlobalSearchBooleanField.HasBuyerExceptions]: "Has Buyer Exceptions",
  [GlobalSearchBooleanField.HasVendorExceptions]: "Has Vendor Exceptions",
  [GlobalSearchBooleanField.IsSold]: "Sold",
} as const satisfies Record<
  ValueInObj<typeof ClearingSaleGlobalSearchBooleanFields>,
  string
>;

const InverseClearingSaleGlobalSearchBooleanLabels = {
  [GlobalSearchBooleanField.HasBuyerExceptions]: "Has No Buyer Exceptions",
  [GlobalSearchBooleanField.HasVendorExceptions]: "Has No Vendor Exceptions",
  [GlobalSearchBooleanField.IsSold]: "Is Not Sold",
} as const satisfies Record<
  ValueInObj<typeof ClearingSaleGlobalSearchBooleanFields>,
  string
>;

export const GlobalSearchFields = {
  Agency: "agency",
  AuctionPen: "auctionPen",
  Buyer: "buyer",
  BuyerAndBuyerWay: "buyerHash",
  BuyerPic: "buyerPic",
  BuyerStatus: "buyerStatus",
  Checkpoint: "checkpoint",
  DeliveryPen: "deliveryPen",
  ExportSites: "ExportSites",
  Label: "label",
  Lane: "lane",
  Miscellaneous: "miscellaneous",
  Marks: "marks",
  OverflowPen: "overflowPen",
  PaymentMethod: "paymentMethod",
  PenScanLot: "penScanLot",
  PricingType: "pricingType",
  SaleRound: "saleRound",
  ReceivalLot: "receivalLot",
  ReceivalPen: "receivalPen",
  Scan: "scan",
  ThirdParty: "thirdParty",
  Vendor: "vendor",
  VendorNumber: "vendorNumber",
  VendorPic: "vendorPic",
  WeighLot: "weighLot",
  WeighLotScan: "weighLotScan",
  ...GlobalSearchBooleanField,
  ...GlobalIntegrationsBooleanFields,
} as const;
export const FieldToGroupLabelDescriptionMap = {
  [GlobalSearchFields.Agency]: "Agencies",
  [GlobalSearchFields.AuctionPen]: "Auction Pens",
  [GlobalSearchFields.Buyer]: "Buyers",
  [GlobalSearchFields.BuyerPic]: "Buyer PICs",
  [GlobalSearchFields.BuyerStatus]: "Buyer Status",
  [GlobalSearchFields.Checkpoint]: "Checkpoints",
  [GlobalSearchFields.DeliveryPen]: "Delivery Pens",
  [GlobalSearchFields.ExportSites]: "Export Sites",
  [GlobalSearchFields.Label]: "Labels",
  [GlobalSearchFields.Lane]: "Lanes",
  [GlobalSearchFields.Miscellaneous]: "Misc.",
  [GlobalSearchFields.PenScanLot]: "Pen Scan Lot",
  [GlobalSearchFields.Marks]: "Marks",
  [GlobalSearchFields.OverflowPen]: "Overflow Pens",
  [GlobalSearchFields.PaymentMethod]: "Payment Type",
  [GlobalSearchFields.PricingType]: "Pricing Types",
  [GlobalSearchFields.ReceivalLot]: "Receival Lot",
  [GlobalSearchFields.ReceivalPen]: "Receival Pens",
  [GlobalSearchFields.SaleRound]: "Sale Rounds",
  [GlobalSearchFields.Scan]: "Scans",
  [GlobalSearchFields.ThirdParty]: "Third Parties",
  [GlobalSearchFields.Vendor]: "Vendors",
  [GlobalSearchFields.VendorNumber]: "Vendor Numbers",
  [GlobalSearchFields.VendorPic]: "Vendor PICs",
  [GlobalSearchFields.BuyerAndBuyerWay]: "Buyer - Buyer Ways",

  [SaleSearchField.SaleId]: "Id",
  [SaleSearchField.Type]: "Type",
  [SaleSearchField.Saleyard]: "Saleyard",
  [SaleSearchField.BaseStatus]: "Base Status",
  [SaleSearchField.Status]: "Status",
  [SaleSearchField.Species]: "Species",
  [SaleSearchField.Date]: "Date",
  [SaleSearchField.Title]: "Title",
  [SaleSearchField.Managers]: "Manager",
  [SaleSearchField.Assignees]: "Assignee",
  [SaleSearchField.EContractStatus]: "eContract Status",
} as const satisfies Partial<
  Record<ValueInObj<typeof GlobalSearchFields> | SaleSearchField, string>
>;

export const FieldToLabelDescriptionMap = {
  [GlobalSearchFields.Agency]: "Agency: ",
  [GlobalSearchFields.AuctionPen]: "Auction Pen: ",
  [GlobalSearchFields.Buyer]: "Buyer: ",
  [GlobalSearchFields.BuyerPic]: "Buyer PIC: ",
  [GlobalSearchFields.BuyerStatus]: "Buyer Status: ",
  [GlobalSearchFields.Checkpoint]: "Checkpoint: ",
  [GlobalSearchFields.DeliveryPen]: "Delivery Pen: ",
  [GlobalSearchFields.ExportSites]: "ExportSites: ",
  [GlobalSearchFields.Label]: "Label: ",
  [GlobalSearchFields.Lane]: "Lane: ",
  [GlobalSearchFields.Marks]: "Marks: ",
  [GlobalSearchFields.OverflowPen]: "Overflow Pen: ",
  [GlobalSearchFields.PaymentMethod]: "Payment Type: ",
  [GlobalSearchFields.PricingType]: "Pricing Type: ",
  [GlobalSearchFields.ReceivalPen]: "Receival Pen: ",
  [GlobalSearchFields.SaleRound]: "Sale Round: ",
  [GlobalSearchFields.Scan]: "Scan: ",
  [GlobalSearchFields.ThirdParty]: "Third Party: ",
  [GlobalSearchFields.Vendor]: "Vendor: ",
  [GlobalSearchFields.VendorNumber]: "Vendor Number: ",
  [GlobalSearchFields.VendorPic]: "Vendor PIC: ",
  [GlobalSearchFields.BuyerAndBuyerWay]: "Buyer - Buyer Way: ",
  [SaleSearchField.Managers]: "Manager: ",
  [SaleSearchField.Assignees]: "Assignee: ",
} as const satisfies Partial<
  Record<ValueInObj<typeof GlobalSearchFields> | SaleSearchField, string>
>;

interface GlobalSearchBooleanOption {
  label: string;
  value: {
    field: string;
    value: boolean;
  };
  trueText?: string;
  falseText?: string;
}

type BooleanSearchFieldLabels = Partial<
  Record<GlobalSearchBooleanField | ExportSite, string>
>;

export const GlobalSearchBooleanOptions = (
  currentSale: Sale,
  searchField: string,
): GlobalSearchBooleanOption[] => {
  const exportSites = getSaleExportSites(currentSale);
  const isClearingSale = currentSale.sale_type === SaleType.CLEARING;
  const isExportSitesSearch = searchField === GlobalSearchFields.ExportSites;

  const searchBooleanFields = isClearingSale
    ? ClearingSaleGlobalSearchBooleanFields
    : GlobalSearchBooleanField;

  const searchBooleanLabels: BooleanSearchFieldLabels = isExportSitesSearch
    ? GlobalIntegrationsBooleanLabels
    : isClearingSale
      ? ClearingSaleGlobalSearchBooleanLabels
      : GlobalSearchBooleanLabels;

  const inverseSearchBooleanLabels: BooleanSearchFieldLabels =
    isExportSitesSearch
      ? InverseGlobalIntegrationsBooleanLabels
      : isClearingSale
        ? InverseClearingSaleGlobalSearchBooleanLabels
        : InverseGlobalSearchBooleanLabels;

  const searchFields = isExportSitesSearch
    ? GlobalIntegrationsBooleanFields
    : searchBooleanFields;

  return Object.values(searchFields)
    .filter(field => (isExportSitesSearch ? exportSites.includes(field) : true))
    .reduce<GlobalSearchBooleanOption[]>((acc, field) => {
      acc.push({
        label: searchBooleanLabels[field],
        value: {
          field,
          value: true,
        },
        trueText: isExportSitesSearch ? "Is Compliant" : "Yes",
      });
      acc.push({
        label: inverseSearchBooleanLabels[field],
        value: {
          field,
          value: false,
        },
        falseText: isExportSitesSearch ? "Is Not Compliant" : "No",
      });
      return acc;
    }, []);
};

export const GlobalSearchPricingTypeOptions = ALL_PRICING_TYPES.map(
  pricingType => ({
    label: LivestockSalePricingTypeDisplayNameLkp[pricingType],
    value: {
      field: GlobalSearchFields.PricingType,
      value: pricingType,
    },
  }),
);

export const GlobalSearchBuyerStatusOptions = ClearingSaleBuyerStatusOrder.map(
  status => ({
    label: ClearingSaleBuyerStatusLabel[status],
    value: {
      field: GlobalSearchFields.BuyerStatus,
      value: status,
    },
  }),
);

export const GlobalSearchPaymentMethodOptions = ALL_PAYMENT_METHODS.map(
  paymentMethod => ({
    label: paymentMethod,
    value: {
      field: GlobalSearchFields.PaymentMethod,
      value: paymentMethod,
    },
  }),
);

export const MiscellaneousLabel = "Miscellaneous";

export const GlobalSearchPlaceholder = "GlobalSearchPlaceholder";
